"use client";

import { useState, useEffect } from "react";
import { getCookie, setCookie } from "@/utils/cookie";
import { X, Settings } from "lucide-react";

interface CookieConsentBannerProps {
  setConsent: (consent: string) => void;
  isOpen?: boolean;
}

export const CookieConsentBanner: React.FC<CookieConsentBannerProps> = ({
  setConsent,
  isOpen = false,
}) => {
  const [isVisible, setIsVisible] = useState(isOpen || false);

  useEffect(() => {
    // This effect runs only once when the component mounts
    const consent = getCookie("korrero_cookie_consent");
    if (!consent || (consent != "true" && consent != "false")) {
      // If no consent cookie is found, make the banner visible
      setIsVisible(true);
    }
  }, []);

  const handleAccept = () => {
    setCookie("korrero_cookie_consent", "true", 365);
    setIsVisible(false);
    setConsent("true");
  };

  const handleReject = () => {
    setCookie("korrero_cookie_consent", "false", 365);
    setIsVisible(false);
    setConsent("false");
  };

  const handleClose = () => {
    if (getCookie("korrero_cookie_consent") == null) {
      setConsent("false");
    }
    setIsVisible(false);
  };

  if (!isVisible) {
    // Render a small icon in the bottom right corner for changing the consent
    return (
      <div className="fixed bottom-10 right-4 z-50 w-10 h-10 rounded-full bg-theme-card border border-theme shadow-lg flex items-center justify-center cursor-pointer hover:bg-theme-card/80 transition-colors transition-transform duration-300 hover:scale-105 hover:shadow-2xl hover:border-theme-primary hover:bg-[var(--color-text-primary)]/10">
        <button
          onClick={() => setIsVisible(true)}
          className="w-full h-full flex items-center justify-center text-theme-primary hover:text-theme-primary/80"
          title="Change cookie consent"
        >
          <Settings size={20} />
        </button>
      </div>
    );
  }

  return (
    <div className="fixed bottom-10 right-4 z-50 max-w-md w-[calc(100vw-2rem)] bg-[var(--color-bg-paper)]/40 backdrop-blur-md border border-theme rounded-2xl shadow-xl p-6">
      {/* Close button */}
      <div className="absolute top-4 right-4">
        <button
          onClick={handleClose}
          className="text-theme-secondary cursor-pointer hover:text-theme-primary hover:bg-[var(--color-bg-inverse-paper)]/10 rounded-full p-1 transition-colors"
          title="Reject cookies"
        >
          <X size={22} />
        </button>
      </div>

      {/* Content */}
      <div className="text-left max-w-sm">
        <h3 className="text-lg font-medium mb-2 text-theme-primary">
          We Value Your Privacy.
        </h3>
        <p className="text-sm text-theme-secondary mb-4 leading-relaxed">
          We use cookies to improve your experience. We use essential cookies to
          make our site work. With your consent, we may also use non-essential
          cookies to analyze traffic.
          <br />
          For more information, please see our{" "}
          <a
            href="/legal/cookie-policy"
            target="_blank"
            rel="noopener noreferrer"
            className="group relative inline-flex items-center text-primary hover:text-primary-light border-b leading-none border-transparent hover:border-primary-light transition-colors"
          >
            <span>Cookie Policy</span>
            <span className="absolute -top-1 -right-2 text-[10px] opacity-0 transition-opacity group-hover:opacity-100">
              ↗
            </span>
          </a>
          .
        </p>

        {/* Buttons */}
        <div className="flex gap-3 justify-between">
          <button
            onClick={handleReject}
            className="px-4 py-2 text-sm cursor-pointer border border-theme rounded-lg text-theme-primary hover:bg-primary hover:text-white transition-colors"
            title="Reject cookies"
          >
            Reject
          </button>
          <button
            onClick={handleAccept}
            className="px-4 py-2 text-sm cursor-pointer border border-theme rounded-lg text-theme-primary hover:bg-primary hover:text-white transition-colors"
            title="Accept cookies"
          >
            Accept
          </button>
        </div>
      </div>
    </div>
  );
};
